import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from "react";
import { ApiError, apiGet } from "../../lib/api";
import { cn } from "../../lib/utils";
import {
  ficheViewerSchema,
  type MessageViewer,
  pageMessagesSchema,
  RECHERCHE_MESSAGES_MAX,
} from "../contract";
import { SignIn } from "../sign-in";
import { BlocRenommee } from "../viewers/renommee";
import {
  ANCIEN_JAMAIS_VENU,
  ANCIEN_PREMIERE_VENUE,
  ANCIEN_TITRE,
  avatarAlt,
  BADGE_ABONNE,
  BADGE_FOLLOWER,
  BOURSE_TITRE,
  BOURSE_VIDE,
  CHIFFRES_LABELS,
  dateLongue,
  FICHE_CHARGEMENT,
  FICHE_ERREUR,
  FICHE_FERMER,
  FICHE_INTROUVABLE,
  FICHE_PAS_FOLLOWER,
  FICHE_PREMIER_PASSAGE,
  FICHE_RETOUR,
  FICHE_TITRE,
  GAINS_TITRE,
  GAINS_VIDE,
  groupeDate,
  groupeTitre,
  MESSAGES_AUCUN,
  MESSAGES_AUCUN_RESULTAT,
  MESSAGES_CHARGEMENT,
  MESSAGES_CHERCHER,
  MESSAGES_CHERCHER_PLACEHOLDER,
  MESSAGES_ERREUR,
  MESSAGES_HORS_STREAM,
  MESSAGES_TITRE,
  MESSAGES_VOIR_PLUS,
  messageQuand,
  nombre,
} from "./fiche-texts";

/**
 * La fiche d'un viewer (`GET /api/direct/viewers/{id}`, ses messages sous `/messages`), d'apres la planche 3 des
 * maquettes validees le 20 octobre. Deux presentations, une seule fiche :
 * - avec `onFermer` : un panneau lateral (volet fixe a droite au grand ecran, plein ecran en dessous), que
 *   l'ecran En direct ouvre au clic sur un present ou un top chatter. Echap le ferme ;
 * - sans : la page `/regie/viewers/{id}`, avec un lien de retour vers la regie.
 *
 * Tout ce que le serveur rend s'affiche comme du texte (jamais de HTML injecte), les dates en toutes lettres a
 * l'heure de Paris. Un code d'erreur n'a que sa phrase (`fiche-texts.ts`), jamais le texte du serveur. La bourse
 * et les gains sont poses vides : Q6 (les des) et Q7 (les badges) les remplissent.
 */

/** Le delai de frappe avant qu'une recherche parte. */
export const DELAI_RECHERCHE_MS = 300;

/** Un identifiant de viewer est un `bigint` en texte : des chiffres, et rien qui puisse sortir du chemin. */
const ID_VALIDE = /^\d{1,18}$/;

const PANNEAU =
  "police-texte fixed inset-y-0 right-0 z-40 flex w-full flex-col gap-6 overflow-y-auto border-l-[3px] " +
  "border-[var(--lisere)] bg-card p-5 text-[22px] text-card-foreground shadow-[inset_3px_0_0_var(--border)] " +
  "md:w-[720px] md:max-w-full md:p-9 md:text-[24px]";
const PAGE = "police-texte flex flex-col gap-6 text-[22px] md:text-[24px]";
const ENCART = "flex flex-col gap-3 rounded-md border-2 border-border bg-background px-5 py-4";
const TITRE_ENCART = "police-titre m-0 text-[28px] font-normal text-primary";

/** Le blason d'un viewer sans avatar (ou dont l'image ne charge pas) : son initiale dans le medaillon dore. */
function Medaillon({ nom, avatar }: { nom: string; avatar: string | null }): ReactElement {
  // `key` sur l'adresse : une autre image repart d'un etat sain.
  const [echec, setEchec] = useState<string | null>(null);
  const image = avatar !== null && echec !== avatar;
  const initiale = Array.from(nom.trim())[0]?.toUpperCase() ?? "?";
  return (
    <div className="relative size-[132px] flex-none rounded-full bg-gradient-to-br from-[#FCDD8E] to-[var(--lisere)] p-[5px] shadow-[0_0_0_3px_var(--background),0_0_0_5px_#6E3570]">
      {image ? (
        <img
          src={avatar}
          alt={avatarAlt(nom)}
          referrerPolicy="no-referrer"
          loading="lazy"
          width={122}
          height={122}
          onError={() => setEchec(avatar)}
          className="block size-[122px] rounded-full object-cover"
        />
      ) : (
        <div
          data-testid="blason"
          aria-hidden="true"
          className="police-nom flex size-[122px] items-center justify-center rounded-full bg-muted text-[64px] leading-none text-primary"
        >
          {initiale}
        </div>
      )}
    </div>
  );
}

function Chiffre({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <li className="rounded-[4px] border-2 border-border bg-background px-3 py-2.5">
      <div className="text-[19px] text-muted-foreground">{label}</div>
      <div className="police-nom text-[40px] leading-none text-[#FCDD8E]">{children}</div>
    </li>
  );
}

type Groupe = { cle: string; titre: string; date: string | null; messages: MessageViewer[] };

/** Les messages du plus recent au plus ancien, regroupes par stream consecutif (« hors stream » pour les autres). */
function grouper(messages: readonly MessageViewer[]): Groupe[] {
  const groupes: Groupe[] = [];
  for (const message of messages) {
    // Hors stream, un groupe par jour (a l'heure de Paris) : sa date est le titre du groupe, pas celle de chaque message.
    const cle =
      message.stream === null
        ? `hors-${dateLongue(message.envoyeLe)}`
        : `stream-${message.stream.id}`;
    const dernier = groupes[groupes.length - 1];
    if (dernier !== undefined && dernier.cle === cle) {
      dernier.messages.push(message);
    } else {
      groupes.push({
        cle,
        titre:
          message.stream === null
            ? MESSAGES_HORS_STREAM
            : groupeTitre(message.stream.titre, message.stream.debut),
        // Un stream sans titre porte deja sa date dans son titre : pas deux fois.
        date:
          message.stream === null
            ? dateLongue(message.envoyeLe)
            : message.stream.titre === null || message.stream.titre.trim() === ""
              ? null
              : groupeDate(message.stream.debut),
        messages: [message],
      });
    }
  }
  return groupes;
}

function MessagesViewer({ viewerId }: { viewerId: string }): ReactElement {
  const [saisie, setSaisie] = useState("");
  const [cherche, setCherche] = useState("");

  useEffect(() => {
    const minuteur = setTimeout(() => setCherche(saisie.trim()), DELAI_RECHERCHE_MS);
    return () => clearTimeout(minuteur);
  }, [saisie]);

  const messages = useInfiniteQuery({
    queryKey: ["direct", "viewer", viewerId, "messages", cherche],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const parametres = new URLSearchParams();
      if (pageParam !== null) parametres.set("avant", pageParam);
      if (cherche !== "") parametres.set("cherche", cherche);
      const requete = parametres.size === 0 ? "" : `?${parametres.toString()}`;
      return apiGet(`/api/direct/viewers/${viewerId}/messages${requete}`, pageMessagesSchema);
    },
    getNextPageParam: (derniere) => derniere.suivant ?? undefined,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const groupes = grouper(messages.data?.pages.flatMap((page) => page.messages) ?? []);

  let corps: ReactElement;
  if (messages.isPending) {
    corps = (
      <p role="status" className="m-0 text-muted-foreground">
        {MESSAGES_CHARGEMENT}
      </p>
    );
  } else if (messages.isError) {
    corps = (
      <p role="alert" className="m-0 text-destructive">
        {MESSAGES_ERREUR}
      </p>
    );
  } else if (groupes.length === 0) {
    corps = (
      <p className="m-0 text-muted-foreground">
        {cherche === "" ? MESSAGES_AUCUN : MESSAGES_AUCUN_RESULTAT}
      </p>
    );
  } else {
    corps = (
      <>
        {groupes.map((groupe) => (
          <div key={`${groupe.cle}-${groupe.messages[0]?.id}`} className="flex flex-col gap-1.5">
            <h4 className="m-0 flex flex-wrap items-baseline justify-between gap-x-4 border-b border-border pb-1 text-[19px] font-normal text-primary">
              <span>{groupe.titre}</span>
              {groupe.date === null ? null : (
                <span className="text-muted-foreground">{groupe.date}</span>
              )}
            </h4>
            {groupe.messages.map((message) => (
              <div
                key={message.id}
                className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 text-[22px]"
              >
                <span className="police-nom text-[24px] text-[var(--input)]">
                  {messageQuand(message.envoyeLe)}
                </span>
                <span className="break-words">{message.texte}</span>
              </div>
            ))}
          </div>
        ))}
        {messages.hasNextPage ? (
          <button
            type="button"
            disabled={messages.isFetchingNextPage}
            onClick={() => void messages.fetchNextPage()}
            className="min-h-[44px] self-start rounded-[4px] border-2 border-[var(--lisere)] bg-transparent px-3.5 text-[20px] text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            {MESSAGES_VOIR_PLUS}
          </button>
        ) : null}
        {messages.isFetchNextPageError ? (
          <p role="alert" className="m-0 text-destructive">
            {MESSAGES_ERREUR}
          </p>
        ) : null}
      </>
    );
  }

  return (
    <section className={ENCART} aria-labelledby="fiche-messages">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 id="fiche-messages" className={TITRE_ENCART}>
          {MESSAGES_TITRE}
        </h3>
        <label className="flex items-center gap-2 text-[19px] text-muted-foreground">
          {MESSAGES_CHERCHER}
          <input
            type="search"
            value={saisie}
            maxLength={RECHERCHE_MESSAGES_MAX}
            placeholder={MESSAGES_CHERCHER_PLACEHOLDER}
            onChange={(event) => setSaisie(event.target.value)}
            className="police-texte w-[200px] rounded-[4px] border-2 border-input bg-card px-2.5 py-1.5 text-[19px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
      </div>
      {corps}
    </section>
  );
}

function Contenu({ viewerId, page }: { viewerId: string; page: boolean }): ReactElement {
  const fiche = useQuery({
    queryKey: ["direct", "viewer", viewerId],
    queryFn: () => apiGet(`/api/direct/viewers/${viewerId}`, ficheViewerSchema),
    enabled: ID_VALIDE.test(viewerId),
    retry: false,
  });

  if (!ID_VALIDE.test(viewerId)) {
    return <p className="m-0 text-destructive">{FICHE_INTROUVABLE}</p>;
  }
  if (fiche.isPending) {
    return (
      <p role="status" className="m-0 text-muted-foreground">
        {FICHE_CHARGEMENT}
      </p>
    );
  }
  if (fiche.isError) {
    const statut = fiche.error instanceof ApiError ? fiche.error.status : null;
    if (statut === 401 || statut === 403) return <SignIn callbackURL="/regie" />;
    return (
      <p role="alert" className="m-0 text-destructive">
        {statut === 404 ? FICHE_INTROUVABLE : FICHE_ERREUR}
      </p>
    );
  }

  const v = fiche.data;
  // Le nom est le h1 de la page ; en panneau, l'ecran En direct a deja le sien.
  const Nom = page ? "h1" : "h2";
  return (
    <>
      <div className="flex flex-wrap items-center gap-6">
        <Medaillon key={v.avatar ?? "sans-avatar"} nom={v.nom} avatar={v.avatar} />
        <div className="flex min-w-0 flex-col gap-1.5">
          <Nom className="police-nom m-0 break-words text-[56px] font-normal leading-none text-[#FCDD8E]">
            {v.nom}
          </Nom>
          <span className="break-all text-muted-foreground">twitch.tv/{v.login}</span>
          {v.follow !== null || v.subs > 0 ? (
            <div className="mt-1 flex gap-2 text-[20px]">
              {v.follow !== null ? (
                <span className="rounded-[3px] border-2 border-[#7FA0C8] px-2.5 py-0.5 text-[#B9CDE6]">
                  {BADGE_FOLLOWER}
                </span>
              ) : null}
              {v.subs > 0 ? (
                <span className="rounded-[3px] border-2 border-primary px-2.5 py-0.5 text-[#FCDD8E]">
                  {BADGE_ABONNE}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <p className="m-0 text-[#E6DAF0]">
        {FICHE_PREMIER_PASSAGE}{" "}
        <span className="police-nom text-[30px] text-[#FCDD8E]">
          {dateLongue(v.premierPassage)}
        </span>
      </p>

      <ul aria-label="Cumuls" className="m-0 grid list-none grid-cols-2 gap-3 p-0 md:grid-cols-3">
        <Chiffre label={CHIFFRES_LABELS.streams}>{nombre(v.streams)}</Chiffre>
        <Chiffre label={CHIFFRES_LABELS.minutes}>{nombre(v.minutes)}</Chiffre>
        <Chiffre label={CHIFFRES_LABELS.messages}>{nombre(v.messages)}</Chiffre>
        <Chiffre label={CHIFFRES_LABELS.bits}>{nombre(v.bits)}</Chiffre>
        <Chiffre label={CHIFFRES_LABELS.follow}>
          <span className={v.follow === null ? "text-[26px]" : "text-[28px]"}>
            {v.follow === null ? FICHE_PAS_FOLLOWER : dateLongue(v.follow)}
          </span>
        </Chiffre>
        <Chiffre label={CHIFFRES_LABELS.subs}>{nombre(v.subs)}</Chiffre>
      </ul>

      <BlocRenommee renommee={v.renommee} viewerId={v.id} />

      {v.ancien === null ? null : (
        <section className={ENCART} aria-labelledby="fiche-ancien">
          <h3 id="fiche-ancien" className={TITRE_ENCART}>
            {ANCIEN_TITRE}
          </h3>
          <p className="m-0">
            {ANCIEN_PREMIERE_VENUE}{" "}
            <span className="police-nom text-[28px] text-[#FCDD8E]">
              {v.ancien.premiereVenue === null
                ? ANCIEN_JAMAIS_VENU
                : dateLongue(v.ancien.premiereVenue)}
            </span>
          </p>
          <ul className="m-0 grid list-none grid-cols-3 gap-3 p-0">
            <Chiffre label={CHIFFRES_LABELS.streams}>{nombre(v.ancien.streams)}</Chiffre>
            <Chiffre label={CHIFFRES_LABELS.messages}>{nombre(v.ancien.messages)}</Chiffre>
            <Chiffre label={CHIFFRES_LABELS.bits}>{nombre(v.ancien.bits)}</Chiffre>
          </ul>
        </section>
      )}

      <section className={cn(ENCART, "border-[var(--lisere)]")} aria-labelledby="fiche-bourse">
        <h3 id="fiche-bourse" className={TITRE_ENCART}>
          {BOURSE_TITRE}
        </h3>
        <p className="m-0 text-muted-foreground">{BOURSE_VIDE}</p>
      </section>

      <section className={ENCART} aria-labelledby="fiche-gains">
        <h3 id="fiche-gains" className={TITRE_ENCART}>
          {GAINS_TITRE}
        </h3>
        <p className="m-0 text-muted-foreground">{GAINS_VIDE}</p>
      </section>

      <MessagesViewer viewerId={viewerId} />
    </>
  );
}

export function FicheViewer(props: { viewerId: string; onFermer?: () => void }): ReactElement {
  const { viewerId, onFermer } = props;
  const enPanneau = onFermer !== undefined;
  // Le dernier `onFermer`, sans reabonner l'ecouteur a chaque rendu du parent.
  const fermer = useRef(onFermer);
  fermer.current = onFermer;
  const boutonFermer = useRef<HTMLButtonElement>(null);

  // A l'ouverture, la mise au point va au bouton de fermeture : Echap se joue alors dans le panneau.
  useEffect(() => {
    if (enPanneau) boutonFermer.current?.focus();
  }, [enPanneau]);

  if (enPanneau) {
    return (
      <aside
        aria-label={FICHE_TITRE}
        className={PANNEAU}
        onKeyDown={(event) => {
          // Un Echap deja traite plus bas (liste de categories, champ) ne ferme pas la fiche.
          if (event.key === "Escape" && !event.defaultPrevented) fermer.current?.();
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-[22px] text-muted-foreground">{FICHE_TITRE}</span>
          <button
            ref={boutonFermer}
            type="button"
            aria-label={FICHE_FERMER}
            onClick={onFermer}
            className="flex size-12 items-center justify-center rounded-[4px] border-2 border-[#7F6AA8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="2.5" fill="none" />
            </svg>
          </button>
        </div>
        <Contenu viewerId={viewerId} page={false} />
      </aside>
    );
  }
  return (
    <article aria-label={FICHE_TITRE} className={PAGE}>
      <a
        href="/regie"
        className="w-fit text-[20px] text-primary no-underline hover:text-[#FCDD8E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {FICHE_RETOUR}
      </a>
      <Contenu viewerId={viewerId} page />
    </article>
  );
}
